import FigmaIcon from "@/assets/figma-icon.svg";
import GitlabIcon from "@/assets/gitlab-icon.svg";
import JiraIcon from "@/assets/jira-icon.svg";
import LinearIcon from "@/assets/linear-icon.svg";
import NotionIcon from "@/assets/notion-icon.svg";
import SentryIcon from "@/assets/sentry-icon.svg";
import SlackIcon from "@/assets/slack-icon.svg";
import { Github } from "lucide-react";
import type { ComponentType } from "react";
import type { Service } from "./integrations.types";

type ServiceMeta = {
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** One-line pitch shown in the Add-new list. */
  blurb: string;
  /** What the user grants when authorizing, shown on the authorize step. */
  grant: string;
};

// Presentation for each connectable service. `label`/`icon` also exist in the
// DB `public.tools` table (and reach the frontend via CR-63's
// CatalogTool.software) — kept here as a static map because the connect flow
// needs the icon component and the authorize copy, which the DB row doesn't
// carry. Keep the `Service` union, this record, and the `tool_id` CHECK on
// integration_contexts in sync when adding a service.
const SERVICES: Record<Service, ServiceMeta> = {
  github: {
    label: "GitHub",
    icon: Github,
    blurb: "Code review, documentation generation and repository analysis.",
    grant: "Grant access to your repositories for code review and documentation.",
  },
  gitlab: {
    label: "GitLab",
    icon: GitlabIcon,
    blurb: "Browse repositories, branches and merge requests; create issues and comments.",
    grant:
      "Grant read and write access to your GitLab projects, branches, merge requests, and issues.",
  },
  slack: {
    label: "Slack",
    icon: SlackIcon,
    blurb: "Post messages, read channels and collaborate with your team.",
    grant: "Grant read and write access to your Slack workspace.",
  },
  sentry: {
    label: "Sentry",
    icon: SentryIcon,
    blurb: "Error monitoring, issue tracking and project analysis.",
    grant: "Grant read access to your Sentry organization, projects, and error events.",
  },
  figma: {
    label: "Figma",
    icon: FigmaIcon,
    blurb: "Bring design files, frames, comments and components into project documents.",
    grant:
      "Grant read access to the design files, comments and published components you can access.",
  },
  notion: {
    label: "Notion",
    icon: NotionIcon,
    blurb: "Attach Notion pages to change requests as linked references.",
    grant: "Grant read access to the pages you share with the Kairo connection.",
  },
  jira: {
    label: "Jira",
    icon: JiraIcon,
    blurb: "Send change requests to Jira Cloud as issues and follow their status.",
    grant: "Grant access to create and read issues in the Jira sites you can access.",
  },
  linear: {
    label: "Linear",
    icon: LinearIcon,
    blurb: "Send change requests to Linear as issues and follow their state.",
    grant: "Grant access to read your Linear workspace and create and update issues in it.",
  },
};

/** Every service connectable on the platform, in display order. */
export const AVAILABLE_SERVICES = Object.keys(SERVICES) as readonly Service[];

export const serviceLabel = (service: Service): string => SERVICES[service].label;
/**
 * The display name of a service id that may not be in the catalog — a service
 * can exist in the database (or in a stored permission set) before this map
 * learns about it, and showing the raw id beats dropping it.
 */
export const serviceDisplayName = (service: string): string =>
  isKnownService(service) ? serviceLabel(service) : service;
export const serviceIcon = (service: Service): ComponentType<{ className?: string }> =>
  SERVICES[service].icon;
export const serviceBlurb = (service: Service): string => SERVICES[service].blurb;
export const serviceGrantDescription = (service: Service): string => SERVICES[service].grant;

export function isKnownService(value: string): value is Service {
  // hasOwn, not `in`: a stored id of "constructor" or "toString" would
  // otherwise resolve to a prototype member and crash the label lookup.
  return Object.hasOwn(SERVICES, value);
}
